import { useEffect, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { TimestampSchema, type Timestamp } from '@bufbuild/protobuf/wkt'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, Lock, Unlock } from 'lucide-react'
import { toast } from 'sonner'
import {
  adminGetBillingLaunchConfig,
  adminUpdateBillingLaunchConfig,
} from '@/gen/cortado/v1/admin-AdminService_connectquery'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DateTimeInput } from '@/components/ui/datetime-input'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute('/_admin/billing/launch')({
  component: BillingLaunchPage,
})

function timestampToUTCInput(timestamp?: Timestamp): string {
  if (!timestamp) return ''
  return new Date(Number(timestamp.seconds) * 1000).toISOString().slice(0, 16)
}

function utcInputToTimestamp(value: string): Timestamp | undefined {
  if (!value) return undefined
  const instant = new Date(`${value}:00Z`)
  if (Number.isNaN(instant.getTime())) return undefined
  return create(TimestampSchema, { seconds: BigInt(Math.floor(instant.getTime() / 1000)) })
}

function formatServerTime(timestamp?: Timestamp): string {
  if (!timestamp) return 'Unavailable'
  return new Date(Number(timestamp.seconds) * 1000).toISOString()
}

function BillingLaunchPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error } = useQuery(adminGetBillingLaunchConfig, {})
  const [launchAt, setLaunchAt] = useState('')
  const [baseline, setBaseline] = useState('')

  useEffect(() => {
    if (!data) return
    const value = timestampToUTCInput(data.config?.billingLaunchAt)
    setLaunchAt(value)
    setBaseline(value)
  }, [data])

  const mutation = useMutation(adminUpdateBillingLaunchConfig, {
    onSuccess: (response) => {
      const value = timestampToUTCInput(response.config?.billingLaunchAt)
      setLaunchAt(value)
      setBaseline(value)
      toast.success(value ? 'Billing launch instant saved' : 'Billing launch instant cleared')
      void queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({ schema: adminGetBillingLaunchConfig, cardinality: undefined }),
      })
    },
    onError: (mutationError) => {
      toast.error(mutationError.message || 'Failed to update billing launch instant')
    },
  })

  const locked = data?.locked ?? false
  const dirty = launchAt !== baseline
  const save = () => mutation.mutate({ billingLaunchAt: utcInputToTimestamp(launchAt) })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Billing launch</h1>
        <p className="text-sm text-muted-foreground">
          The single backend-enforced UTC boundary for subscription-required access.
        </p>
      </div>

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Unable to load billing launch configuration'}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>UTC launch instant</CardTitle>
                <CardDescription>
                  Before this instant, unsubscribed organizations retain full access. At exact equality and afterward, they become indefinitely read-only.
                </CardDescription>
              </div>
              <Badge variant={locked ? 'destructive' : 'secondary'}>
                {locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                {locked ? 'Locked' : 'Editable'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-2 max-w-md">
              <Label htmlFor="billing-launch-at">Billing launch at (UTC)</Label>
              <DateTimeInput
                id="billing-launch-at"
                value={launchAt}
                onChange={(event) => setLaunchAt(event.target.value)}
                disabled={isLoading || locked || mutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                A future instant may be set, moved, or cleared. Once server time reaches it, the API permanently rejects edits.
              </p>
            </div>

            <div className="rounded-md border p-3 text-sm">
              <span className="text-muted-foreground">Backend server time: </span>
              <span className="font-mono">{formatServerTime(data?.serverTime)}</span>
            </div>

            <div className="flex gap-2">
              <Button onClick={save} disabled={!dirty || locked || mutation.isPending || isLoading}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save launch instant
              </Button>
              <Button
                variant="outline"
                onClick={() => setLaunchAt('')}
                disabled={locked || mutation.isPending || isLoading || !launchAt}
              >
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
